
import { defineComponent, onBeforeMount, reactive, Transition } from 'vue'

// components
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { CompanyT } from '@/core/types/companies.types'
import { CityT } from '@/core/types/cities.types'
import { ManagerT } from '@/core/types/users.types'

// realisations
import { companies } from '@/core/realisations/companies.realisation'
import { users } from '@/core/realisations/users.realisation'

// constants
import { PAYMENT_TYPE_OPTIONS } from '@/core/constants/create_order.constants'
import { cities } from '@/core/realisations/cities.realisation'
import {
    INVOICE_BILLING,
    INVOICE_CALCULATION,
    HIDE_DOP_INVOICES_OPTIONS,
    INVOICE_CANCEL,
    INVOICE_PAYMENT_METHODS,
    INVOICE_ADDITIONAL_SERVICES,
    SELF_DELIVERY_OPTIONS,
    PICKUP_OPTIONS,
} from '@/core/constants/invoices.constants'

// composable
import useAccount from '@/common/composable/useAccount'
import useInvoicesFilters from '@/modules/OrdersHistoryModule/composable/useInvoicesFilters'
import sharedFilters from '@/common/composable/useSharedFiltersValues'

import { invoices } from '@/core/realisations/invoices.realisation'
import { InvoiceStatus } from '@/core/types/invoices.types'

const MANAGER_ROLE_ID = 20

const SHIPMENT_TYPES = [
    { id: 1, name: 'Авто' },
    { id: 2, name: 'Авиа' },
    { id: 5, name: 'Экспресс за 3 часа' },
    { id: 6, name: 'Экспресс день в день' }
]

export default defineComponent({
    components: {
        'the-input': TheInput,
        'the-select': TheSelect,
        'the-button': TheButton,
        Transition,
        Checkbox,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['resetted', 'search'],
    setup(props, { emit }) {
        const { haveRole } = useAccount()
        const { filters_state } = useInvoicesFilters()

        const state = reactive({
            is_visible: false,
        })

        const data_state = reactive({
            companies: new DataState<CompanyT>({}),
            cities: new DataState<CityT>({}),
            managers: new DataState<ManagerT>({}),
            statuses: new DataState<InvoiceStatus>({}),
        })

        const reset = () => {
            if (props.loading) return

            emit('resetted')
        }

        const toggle = () => {
            state.is_visible = !state.is_visible
        }

        const search = () => {
            if (props.loading) return

            emit('search')
        }

        const onDateChange = (value: string | null) => {
            filters_state.archive = Number(Boolean(value))
        }

        // CITIES
        const loadCities = async (page: number, search?: string) => {
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                const data = await cities.getAllList(page, search)
                data_state.cities.list = data.data

                data_state.cities.pagination.total_pages = data.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const loadMoreCities = async (page: number) => {
            if (page > data_state.cities.pagination.total_pages) return
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                const response = await cities.getAllList(page)
                data_state.cities.list = data_state.cities.list.concat(response.data)

                data_state.cities.pagination.total_pages = response.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const searchCity = (search_query: string) => {
            // data_state.cities.pagination.current_page
            loadCities(1, search_query)
        }

        const unshiftFindedCity = async (city: CityT) => {
            await loadCities(data_state.cities.pagination.current_page)
            data_state.cities.list.unshift(city)
        }

        // COMPANIES
        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({
                    page,
                    search,
                    isExtended: 0,
                    statusId: 2,
                })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({ page, isExtended: 0, statusId: 2 })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCompanies = (search_query: string) => {
            // data_state.companies.pagination.current_page
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        //

        const loadStatuses = async () => {
            try {
                data_state.statuses.is_loading = true

                const response = await invoices.getInvoiceStatuses()

                data_state.statuses.list = response.data

                data_state.statuses.is_failed = false
                data_state.statuses.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.statuses.failure.catchError(error)

                if (!data_state.statuses.failure.error_state.is_cancel) {
                    data_state.statuses.is_failed = true
                }
            } finally {
                if (!data_state.statuses.failure.error_state.is_cancel) {
                    data_state.statuses.is_loading = false
                }
            }
        }

        // MANAGERS

        const loadManagers = async (page: number, search?: string) => {
            data_state.managers.pagination.current_page = page

            try {
                data_state.managers.is_loading = true

                const response = await users.getManagers({
                    page,
                    name: search,
                    statusId: 1,
                    isExtended: 0,
                })

                data_state.managers.list = response.data
                data_state.managers.pagination.total_pages = response.meta.last_page

                data_state.managers.is_failed = false
                data_state.managers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.managers.failure.catchError(error)

                if (!data_state.managers.failure.error_state.is_cancel) {
                    data_state.managers.is_failed = true
                }
            } finally {
                if (!data_state.managers.failure.error_state.is_cancel) {
                    data_state.managers.is_loading = false
                }
            }
        }

        const loadMoreManagers = async (page: number) => {
            if (page > data_state.managers.pagination.total_pages) return
            data_state.managers.pagination.current_page = page

            try {
                data_state.managers.is_loading = true

                const response = await users.getManagers({ page, statusId: 1, isExtended: 0 })
                data_state.managers.list = data_state.managers.list.concat(response.data)

                data_state.managers.pagination.total_pages = response.meta.last_page

                data_state.managers.is_failed = false
                data_state.managers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.managers.failure.catchError(error)

                if (!data_state.managers.failure.error_state.is_cancel) {
                    data_state.managers.is_failed = true
                }
            } finally {
                if (!data_state.managers.failure.error_state.is_cancel) {
                    data_state.managers.is_loading = false
                }
            }
        }

        const searchManagers = async (search_query: string) => {
            await loadManagers(1, search_query)
        }

        const unshiftFindedManager = (manager: ManagerT) => {
            loadManagers(data_state.managers.pagination.current_page)
            data_state.managers.list.unshift(manager)
        }

        //

        onBeforeMount(() => {
            if (haveRole(MANAGER_ROLE_ID) && sharedFilters.managerId) {
                filters_state.managerId = sharedFilters.managerId
            }

            loadCompanies(1)
            loadCities(1)
            loadManagers(1)
            loadStatuses()
        })

        return {
            state,
            filters_state,
            data_state,
            SHIPMENT_TYPES,
            PAYMENT_TYPE_OPTIONS,
            reset,
            toggle,
            search,
            onDateChange,
            // COMPANIES
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFindedCompany,
            // CITIES
            loadCities,
            loadMoreCities,
            searchCity,
            unshiftFindedCity,
            //
            loadStatuses,
            // MANAGERS
            loadManagers,
            loadMoreManagers,
            searchManagers,
            unshiftFindedManager,
            // CONST
            INVOICE_BILLING,
            INVOICE_CALCULATION,
            HIDE_DOP_INVOICES_OPTIONS,
            INVOICE_CANCEL,
            INVOICE_PAYMENT_METHODS,
            INVOICE_ADDITIONAL_SERVICES,
            SELF_DELIVERY_OPTIONS,
            PICKUP_OPTIONS,
        }
    },
})
